import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useUsuario, UsuarioIncompleto } from "../../Hooks/UsuarioHook";
import { useEntidade, Entidade } from "../../Hooks/EntidadeHook";
import { getTipoEntidade } from "../SeletorContasModal";
import { app_base_url } from "../../Utils/Urls";
import { limpa_mascara_cpf_cnpj } from "../../Utils/Mascara/MascaraCpf";
import { validaCpf } from "../../Utils/Validadores/ValidCpf";
import { validaCnpj } from "../../Utils/Validadores/ValidCnpj";
import { ehCpfDeManutencao } from "../../Utils/permissoes";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Aba = "minhas" | "prefeitura" | "camara" | "consorcio" | "todos";

const normalizar = (t: string) =>
  (t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export const TrocarContaModal: React.FC<Props> = ({ open, onClose }) => {
  const {
    usuario,
    carregarUsuariosPorCpfOuCnpj,
    trocarConta,
    registrarEmEntidade,
  } = useUsuario();
  const { entidades } = useEntidade();

  const [todasContas, setTodasContas] = useState<UsuarioIncompleto[]>([]);
  const [loading, setLoading] = useState(false);
  const [aba, setAba] = useState<Aba>("minhas");
  const [busca, setBusca] = useState("");
  const [confirmar, setConfirmar] = useState<Entidade | null>(null);
  const [processando, setProcessando] = useState(false);
  // Progresso do registro em lote (só manutenção). null = não está rodando.
  const [emLote, setEmLote] = useState<{
    feitas: number;
    total: number;
    falhas: number;
  } | null>(null);
  const [resultadoLote, setResultadoLote] = useState<{
    feitas: number;
    falhas: number;
  } | null>(null);

  // Carrega as contas do usuário ao abrir
  useEffect(() => {
    if (!open) return;
    if (!usuario?.usuario?.id) return;

    const buscar = async () => {
      setLoading(true);
      try {
        const documento = usuario.usuario.cpf || usuario.usuario.cnpj;
        const doclimpo = limpa_mascara_cpf_cnpj(documento);

        let lista: UsuarioIncompleto[] = [];
        if (doclimpo.length === 11 && validaCpf(doclimpo)) {
          lista = await carregarUsuariosPorCpfOuCnpj(doclimpo, "cpf");
        } else if (doclimpo.length === 14 && validaCnpj(doclimpo)) {
          lista = await carregarUsuariosPorCpfOuCnpj(doclimpo, "cnpj");
        }
        setTodasContas(lista);
      } finally {
        setLoading(false);
      }
    };

    buscar();
    setAba("minhas");
    setBusca("");
  }, [open, usuario?.usuario?.id]);

  // entidade_id -> conta do usuário (inclui a conta atual)
  const contaPorEntidade = useMemo(() => {
    const m = new Map<number, UsuarioIncompleto>();
    todasContas.forEach((c) => m.set(c.entidade_id, c));
    return m;
  }, [todasContas]);

  // "Minhas contas": as contas do usuário, exceto a que ele já está logado
  const minhasContas = useMemo(() => {
    const b = normalizar(busca);
    return todasContas
      .filter((c) => c.id !== usuario?.usuario?.id)
      .filter((c) => {
        if (!b) return true;
        const ent = entidades.find((e) => e.id === c.entidade_id);
        return normalizar(ent?.titulo ?? "").includes(b);
      });
  }, [todasContas, entidades, busca, usuario?.usuario?.id]);

  // Entidades filtradas por tipo (aba) + busca
  const entidadesFiltradas = useMemo(() => {
    const b = normalizar(busca);
    return entidades.filter((ent) => {
      const t = normalizar(ent.titulo);
      if (aba === "prefeitura" && !t.includes("prefeitura")) return false;
      if (aba === "camara" && !t.includes("camara")) return false;
      if (aba === "consorcio" && !t.includes("consorcio")) return false;
      if (b && !t.includes(b)) return false;
      return true;
    });
  }, [entidades, aba, busca]);

  const irParaSistemas = () => {
    onClose();
    window.location.href = `${app_base_url}/sistemas`;
  };

  const handleTrocar = async (contaId: number) => {
    const ok = await trocarConta(contaId);
    if (ok) irParaSistemas();
  };

  const handleClicarEntidade = (ent: Entidade) => {
    const conta = contaPorEntidade.get(ent.id);
    if (conta) handleTrocar(conta.id);
    else setConfirmar(ent);
  };

  const handleRegistrar = async () => {
    if (!confirmar) return;
    setProcessando(true);
    const ok = await registrarEmEntidade(confirmar.id);
    setProcessando(false);
    if (ok) {
      setConfirmar(null);
      irParaSistemas();
    }
  };

  /**
   * Só a manutenção: registra a conta em TODAS as entidades que ainda faltam.
   *
   * A lista de entidades vem da API de endereços (useEntidade), não do banco do
   * Login Único — que nem tem tabela de entidade. Por isso prefeitura nova é
   * coberta automaticamente, sem ninguém editar script.
   */
  const daManutencao = ehCpfDeManutencao(usuario?.usuario?.cpf);
  const entidadesFaltando = useMemo(
    () => entidades.filter((ent) => !contaPorEntidade.has(ent.id)),
    [entidades, contaPorEntidade]
  );

  const registrarEmTodas = async () => {
    if (entidadesFaltando.length === 0 || emLote) return;

    setEmLote({ feitas: 0, total: entidadesFaltando.length, falhas: 0 });

    let feitas = 0;
    let falhas = 0;

    // Sequencial de propósito: cada chamada cria conta e grava auditoria.
    // Em paralelo seriam ~60 requisições simultâneas na API de entidades.
    for (const ent of entidadesFaltando) {
      // aplicarSessao = false: sem isso, cada volta trocaria a sessão e no fim
      // você estaria logado na última entidade da lista.
      const ok = await registrarEmEntidade(ent.id, false);

      if (ok) feitas += 1;
      else falhas += 1;

      setEmLote({ feitas, total: entidadesFaltando.length, falhas });
    }

    // Recarrega as contas para a lista refletir o que acabou de ser criado.
    const documento = usuario.usuario.cpf || usuario.usuario.cnpj;
    const doclimpo = limpa_mascara_cpf_cnpj(documento);
    if (doclimpo.length === 11 && validaCpf(doclimpo)) {
      setTodasContas(await carregarUsuariosPorCpfOuCnpj(doclimpo, "cpf"));
    } else if (doclimpo.length === 14 && validaCnpj(doclimpo)) {
      setTodasContas(await carregarUsuariosPorCpfOuCnpj(doclimpo, "cnpj"));
    }

    setEmLote(null);
    setResultadoLote({ feitas, falhas });
  };

  // ---- Cartão de conta (aba "Minhas contas") ----
  const cardConta = (conta: UsuarioIncompleto) => {
    const entidade = entidades.find((e) => e.id === conta.entidade_id);
    const titulo = entidade?.titulo ?? "Entidade desconhecida";
    const tipo = getTipoEntidade(titulo);
    return (
      <CardBase
        key={`conta-${conta.id}`}
        logo={entidade?.logo}
        titulo={titulo}
        tipo={tipo}
        onClick={() => handleTrocar(conta.id)}
        rodape={
          <Typography variant="body2" color="text.secondary" noWrap>
            {conta.nome}
          </Typography>
        }
      />
    );
  };

  // ---- Cartão de entidade (abas por tipo / todos) ----
  const cardEntidade = (ent: Entidade) => {
    const tipo = getTipoEntidade(ent.titulo);
    const conta = contaPorEntidade.get(ent.id);
    const jaTem = Boolean(conta);
    return (
      <CardBase
        key={`ent-${ent.id}`}
        logo={ent.logo}
        titulo={ent.titulo}
        tipo={tipo}
        atenuado={!jaTem}
        onClick={() => handleClicarEntidade(ent)}
        rodape={
          jaTem ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
              <CheckCircleIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontWeight={600}>
                Sua conta
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "primary.main" }}>
              <PersonAddAlt1Icon sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontWeight={600}>
                Registrar-se
              </Typography>
            </Box>
          )
        }
      />
    );
  };

  const itens =
    aba === "minhas" ? minhasContas.map(cardConta) : entidadesFiltradas.map(cardEntidade);

  const vazioTexto =
    aba === "minhas"
      ? "Você não tem outras contas."
      : "Nenhuma entidade encontrada.";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography variant="h5" fontWeight={800}>
          Acessar outra conta
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Acesse uma das suas contas ou registre-se em uma nova entidade.
        </Typography>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Tabs
          value={aba}
          onChange={(_, v) => setAba(v)}
          centered
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab label="Minhas contas" value="minhas" />
          <Tab label="Prefeitura" value="prefeitura" />
          <Tab label="Câmara" value="camara" />
          <Tab label="Consórcio" value="consorcio" />
          <Tab label="Todos" value="todos" />
        </Tabs>

        {/*
          Faixa da MANUTENÇÃO — some para todo mundo que não está na lista de
          CPFs (Utils/permissoes.ts). Registra a conta de uma vez em todas as
          entidades que faltam, para não clicar 60 vezes.
        */}
        {daManutencao && (entidadesFaltando.length > 0 || emLote || resultadoLote) && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                Manutenção
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {emLote
                  ? `Registrando… ${emLote.feitas} de ${emLote.total}${
                      emLote.falhas ? ` · ${emLote.falhas} falha(s)` : ""
                    }`
                  : resultadoLote
                  ? `${resultadoLote.feitas} conta(s) criada(s)${
                      resultadoLote.falhas
                        ? ` · ${resultadoLote.falhas} falha(s)`
                        : ""
                    }`
                  : `Você não tem conta em ${entidadesFaltando.length} entidade(s).`}
              </Typography>
            </Box>

            <Button
              size="small"
              variant="contained"
              disabled={!!emLote || entidadesFaltando.length === 0}
              startIcon={
                emLote ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PersonAddAlt1Icon />
                )
              }
              onClick={registrarEmTodas}
              sx={{ borderRadius: 2, textTransform: "none", whiteSpace: "nowrap" }}
            >
              {emLote
                ? `${emLote.feitas}/${emLote.total}`
                : `Registrar nas ${entidadesFaltando.length} restantes`}
            </Button>
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Pesquisar entidade..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <CentroFixo>
            <CircularProgress />
          </CentroFixo>
        ) : itens.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gridAutoRows: "min-content",
              gap: 1.5,
              height: 520,
              overflowY: "auto",
              pr: 0.5,
              pb: 0.5,
            }}
          >
            {itens}
          </Box>
        ) : (
          <CentroFixo>
            <Typography variant="body1" color="text.secondary">
              {vazioTexto}
            </Typography>
          </CentroFixo>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ justifyContent: "center", py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ px: 4, borderRadius: 2 }}>
          Fechar
        </Button>
      </DialogActions>

      {/* Confirmação de registro numa nova entidade */}
      <Dialog
        open={Boolean(confirmar)}
        onClose={() => !processando && setConfirmar(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              p: 1,
              borderRadius: 2,
              bgcolor: "rgba(79,142,247,0.12)",
              color: "primary.main",
            }}
          >
            <PersonAddAlt1Icon />
          </Box>
          <Typography component="span" sx={{ fontWeight: 800, fontSize: 18 }}>
            Registrar-se
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Você deseja se registrar em{" "}
            <Box component="strong" sx={{ color: "text.primary" }}>
              {confirmar?.titulo}
            </Box>
            ? Sua conta será criada automaticamente com os seus dados e você
            entrará nela.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmar(null)}
            disabled={processando}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleRegistrar}
            disabled={processando}
            startIcon={
              processando ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PersonAddAlt1Icon />
              )
            }
            sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
          >
            {processando ? "Registrando..." : "Sim, registrar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

// ---------- auxiliares de layout ----------

function CentroFixo({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        height: 520,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </Box>
  );
}

interface CardBaseProps {
  logo?: string;
  titulo: string;
  tipo: { label: string; color: string; Icon: React.ElementType };
  onClick: () => void;
  rodape: React.ReactNode;
  atenuado?: boolean;
}

function CardBase({ logo, titulo, tipo, onClick, rodape, atenuado }: CardBaseProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1,
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: "divider",
        cursor: "pointer",
        transition: "all .18s ease",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: "action.hover",
          transform: "translateY(-3px)",
          boxShadow: "0 8px 22px rgba(37,99,235,0.18)",
        },
      }}
    >
      <Avatar
        variant="rounded"
        src={logo}
        alt={titulo}
        sx={{
          width: 72,
          height: 72,
          bgcolor: "background.default",
          color: tipo.color,
          border: 1,
          borderColor: "divider",
          opacity: atenuado ? 0.75 : 1,
          "& img": { objectFit: "contain" },
        }}
      >
        <tipo.Icon fontSize="large" />
      </Avatar>

      <Chip
        size="small"
        label={tipo.label}
        sx={{
          height: 20,
          fontSize: "0.68rem",
          fontWeight: 700,
          color: tipo.color,
          bgcolor: `${tipo.color}1A`,
        }}
      />

      <Typography
        fontWeight={700}
        sx={{
          lineHeight: 1.25,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {titulo}
      </Typography>

      {rodape}
    </Box>
  );
}
